/**
 * BiometricAdapter — the contract every biometric implementation must satisfy.
 *
 * Two implementations exist:
 * - CapacitorBiometric: native (iOS Keychain / Android Keystore via plugin)
 * - WebAuthnBiometric: web (navigator.credentials with platform authenticator)
 *
 * The hook `useBiometricLogin()` selects the implementation at runtime via
 * isNative(). Components consume the hook, never an adapter directly.
 *
 * Error contract: methods throw an Error subclass with `.code` set to one of
 * the BiometricErrorCode values below. `authenticate()` uses USER_CANCEL for
 * user-initiated dismiss (no toast); UNAVAILABLE for hardware/OS-level
 * problems (toast + hide UI); INVALID for stored credential rejected by
 * server (clear and force password re-login).
 */

export const BiometricErrorCode = Object.freeze({
	USER_CANCEL: 'user_cancel',
	UNAVAILABLE: 'unavailable',     // hardware missing or OS biometrics not enrolled
	LOCKOUT: 'lockout',              // too many fails — OS-level lockout
	INVALID: 'invalid',              // credential present but server rejected it
	UNKNOWN: 'unknown',
});

export class BiometricError extends Error {
	constructor(code, message, cause) {
		super(message || code);
		this.code = code;
		this.cause = cause;
		this.name = 'BiometricError';
	}
}

/**
 * @typedef {Object} BiometricAdapter
 * @property {() => Promise<boolean>} isAvailable
 *   True if hardware is present AND OS-level biometric enrollment exists.
 * @property {() => Promise<boolean>} isEnrolled
 *   True if THIS app has stored a credential the user opted into.
 * @property {(jwt: string) => Promise<void>} enroll
 *   Stores the supplied JWT under biometric protection. Throws BiometricError on failure.
 * @property {() => Promise<string>} authenticate
 *   Prompts biometric. On success returns the stored JWT. Throws BiometricError otherwise.
 * @property {() => Promise<void>} disable
 *   Removes the stored credential and clears enrollment flag. Idempotent.
 */

export const BIOMETRIC_ADAPTER = {};  // marker for documentation only
